import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Handshake, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { Loading } from "../../components/Shared";

export default function ServicesPage() {
  const [services, setServices] = useState(null);
  useEffect(() => { api.get("/public/services").then((r) => setServices(r.data)).catch(() => setServices([])); }, []);
  if (!services) return <Loading label="Loading services…" />;
  const included = services.filter((s) => s.category === "included");
  const partner = services.filter((s) => s.category === "partner");
  return (
    <div className="p1-container py-14 lg:py-20" data-testid="services-page">
      <p className="p1-eyebrow">Services</p>
      <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl lg:text-5xl">Everything your parents need, coordinated by one team</h1>
      <p className="mt-4 max-w-3xl text-lg text-stone-600">
        Some services are part of your ParentOne plan. Others are fulfilled by verified third-party partners and charged by them — ParentOne coordinates, accompanies and reports back either way.
      </p>

      <div className="mt-12">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-800" />
          <h2 className="font-heading text-xl font-bold text-stone-900 sm:text-2xl">Included ParentOne services</h2>
        </div>
        <p className="mt-1 text-sm text-stone-500">Delivered and managed directly by your ParentOne Care Manager as part of your plan.</p>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {included.map((s) => (
            <div key={s.id} className="p1-card p-6 transition-shadow hover:shadow-md" data-testid={`service-card-${s.id}`}>
              <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">Included in plan</span>
              <h3 className="mt-3 font-heading text-lg font-semibold text-stone-900">{s.name}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{s.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-14">
        <div className="flex items-center gap-3">
          <Handshake className="h-6 w-6 text-terracotta" />
          <h2 className="font-heading text-xl font-bold text-stone-900 sm:text-2xl">Coordinated with verified partners</h2>
        </div>
        <p className="mt-1 text-sm text-stone-500">Provided and charged by verified third-party partners. ParentOne arranges, supervises and keeps the family informed.</p>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {partner.map((s) => (
            <div key={s.id} className="p1-card p-6 transition-shadow hover:shadow-md" data-testid={`service-card-${s.id}`}>
              <span className="rounded-full bg-terracotta-soft px-2.5 py-0.5 text-xs font-semibold text-terracotta">Verified partner · charged separately</span>
              <h3 className="mt-3 font-heading text-lg font-semibold text-stone-900">{s.name}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{s.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-14 rounded-3xl bg-emerald-950 p-8 text-center text-white sm:p-12">
        <h2 className="font-heading text-2xl font-bold sm:text-3xl">Not sure what your parents need?</h2>
        <p className="mx-auto mt-2 max-w-xl text-stone-300">Start with a free assessment — we'll map the right mix of services for your family.</p>
        <Link to="/onboarding" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-emerald-950 transition-colors hover:bg-stone-100" data-testid="services-cta">Book a Free Assessment <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
}
